import { useEffect, useRef, useState } from "react"
import { PropertyValueSpecification, RasterTileSource } from "maplibre-gl"
import { useGlobalMap } from "features/map/GlobalViewState"
import { LayerStatus } from "./layerStatus"
import { LayerOrderConstraint, resolveBeforeId } from "./layerOrder"

export function useMapRasterLayer(
    enabled: boolean,
    sourceId: string,
    tileUrl: string,
    opts: {
        maxzoom?: number
        opacity?: number | PropertyValueSpecification<number>
        saturation?: number
        persistSource?: boolean
        tileSize?: number
    } & LayerOrderConstraint = {}
): LayerStatus {
    const {
        maxzoom = 8,
        opacity = 0.85,
        saturation = 0,
        persistSource = false,
        tileSize = 256,
        above,
        below,
    } = opts
    const [status, setStatus] = useState<LayerStatus>("idle")
    const aboveKey = above?.join(",")
    const belowKey = below?.join(",")
    const tileUrlRef = useRef(tileUrl)
    tileUrlRef.current = tileUrl
    const appliedTileUrlRef = useRef<string | null>(null)
    const map = useGlobalMap()

    useEffect(() => {
        if (!map) return
        if (!enabled) {
            setStatus("idle")
            return
        }

        const layerId = `${sourceId}-raster`
        const alreadyLoaded =
            persistSource && !!map.getSource(sourceId) && map.isSourceLoaded(sourceId)
        setStatus(alreadyLoaded ? "ready" : "loading")

        const add = () => {
            if (!enabled) return
            try {
                if (!map.getSource(sourceId)) {
                    map.addSource(sourceId, {
                        type: "raster",
                        tiles: [tileUrlRef.current],
                        tileSize,
                        maxzoom,
                    })
                    appliedTileUrlRef.current = tileUrlRef.current
                }
                const beforeId = resolveBeforeId(map, { above, below })
                if (!map.getLayer(layerId)) {
                    map.addLayer(
                        {
                            id: layerId,
                            type: "raster",
                            source: sourceId,
                            paint: {
                                "raster-opacity": opacity,
                                "raster-saturation": saturation,
                            },
                        },
                        beforeId
                    )
                } else {
                    map.moveLayer(layerId, beforeId)
                }
            } catch (e) {
                console.warn(`[overlay] add failed for ${sourceId}:`, e)
                setStatus("error")
            }
        }

        const remove = () => {
            try {
                if (map.getLayer(layerId)) map.removeLayer(layerId)
                if (!persistSource && map.getSource(sourceId)) map.removeSource(sourceId)
            } catch {
                // ignore — map may be mid-teardown
            }
        }

        const checkLoaded = (e: { sourceId?: string }) => {
            if (e.sourceId !== sourceId) return
            if (map.isSourceLoaded(sourceId)) setStatus("ready")
        }

        const onSourceError = (e: { sourceId?: string }) => {
            if (e.sourceId === sourceId) setStatus("error")
        }

        if (map.isStyleLoaded()) add()
        map.on("styledata", add)
        map.on("sourcedata", checkLoaded)
        map.on("error", onSourceError)

        return () => {
            map.off("styledata", add)
            map.off("sourcedata", checkLoaded)
            map.off("error", onSourceError)
            remove()
        }
    }, [
        map,
        enabled,
        sourceId,
        maxzoom,
        opacity,
        saturation,
        persistSource,
        tileSize,
        above,
        below,
        aboveKey,
        belowKey,
    ])

    useEffect(() => {
        if (!enabled) return
        if (appliedTileUrlRef.current === tileUrl) return
        const source = map?.getSource(sourceId)
        if (source instanceof RasterTileSource) {
            source.setTiles([tileUrl])
            appliedTileUrlRef.current = tileUrl
        }
    }, [map, enabled, sourceId, tileUrl])

    return status
}
